import * as React from 'react';

// Keep the existing mouse drop targets usable with touch and pen input.
export function useItemLongPress(enabled: boolean, start: () => void, cancel: () => void) {
  const actions = React.useRef({ start, cancel });
  actions.current = { start, cancel };
  const cleanup = React.useRef<() => void>(() => {});
  React.useEffect(() => () => cleanup.current(), []);

  return (event: React.PointerEvent<HTMLElement>) => {
    if (!enabled || event.button !== 0 || !event.isPrimary) return;
    cleanup.current();
    const { pointerId, pointerType, clientX, clientY } = event;
    let active = false;
    let lastX = clientX, lastY = clientY;
    let clickTimer: ReturnType<typeof setTimeout>;
    const suppressClick = (e: Event) => { e.preventDefault(); e.stopImmediatePropagation(); };
    const mouse = (type: string, x: number, y: number) => {
      const translated=new MouseEvent(type, {
        bubbles: true, cancelable: true, clientX: x, clientY: y, button: 0,
      });
      Object.defineProperty(translated,'itemTouchDrag',{value:true});
      document.elementFromPoint(x, y)?.dispatchEvent(translated);
    };
    const timer = setTimeout(() => {
      active = true;
      actions.current.start();
      document.addEventListener('click', suppressClick, true);
      if (pointerType !== 'mouse') mouse('mousemove', lastX, lastY);
    }, 450);
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      lastX = e.clientX; lastY = e.clientY;
      if (!active && Math.hypot(lastX - clientX, lastY - clientY) > 10) clearTimeout(timer);
      if (active && pointerType !== 'mouse') {
        e.preventDefault();
        mouse('mousemove', lastX, lastY);
      }
    };
    const finish = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      clearTimeout(timer);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      if (active) {
        if (e.type === 'pointercancel') actions.current.cancel();
        else if (pointerType !== 'mouse' && !(e as PointerEvent & {itemTouchDropHandled?:boolean}).itemTouchDropHandled) {
          e.preventDefault();
          mouse('mouseup', e.clientX, e.clientY);
        }
        // Safari can send a compatibility click after touch release.
        clickTimer = setTimeout(() => document.removeEventListener('click', suppressClick, true), 700);
      }
    };
    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', finish, { passive: false });
    document.addEventListener('pointercancel', finish);
    cleanup.current = () => {
      clearTimeout(timer);
      clearTimeout(clickTimer);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      document.removeEventListener('click', suppressClick, true);
    };
  };
}
